// infcode_change - metrics foundation
import * as Log from "@opencode-ai/core/util/log"
import { resolveMetricsVersion } from "./version"
import { resolveMetricsIdentity } from "./identity"
import { loadMetricsRuntime } from "./runtime"
import { METRICS_APP_NAME, resolveMetricsOs, resolveMetricsPlatform } from "./platform"
import { isMetricsTestProcess, resolveMetricsSdkEnv, resolveMetricsTrackingEnv } from "./env"
import { sanitizeDetail, ERROR_DETAIL_LIMIT, PANIC_STACK_LIMIT } from "./sanitize"
import { STATUS_CODE, type MetricsStatus, type RegisteredMetricUri } from "./uris"
import { AuthStorage } from "../auth/storage"
import { objectRecord, trimmedText, userIdFrom } from "./text"
import type {
  MetricsEventBuilder,
  MetricsRuntimeClient,
  MetricsRuntimeModule,
  MetricsTraceEvent,
} from "./runtime-types"

const LOG_PREFIX = "[metrics:client]"
const log = Log.create({ service: LOG_PREFIX })

const DEFAULT_FLUSH_INTERVAL_MS = 30_000
const DEFAULT_MAX_PENDING = 5_000
const FLUSH_TIMEOUT_MS = 2_000
const SHUTDOWN_TIMEOUT_MS = 3_000

export type EventSpec =
  | { kind: "page_view"; uri: RegisteredMetricUri; sessionID?: string; traceID?: string }
  | {
      kind: "performance"
      uri: RegisteredMetricUri
      value?: number
      unit?: string
      status?: MetricsStatus
      model?: string
      provider?: string
      sessionID?: string
      traceID?: string
      /** 任意 JSON 业务载荷（event_tracking_rs 0.2.0 metadata）；旧版 SDK 无此方法时自动忽略。 */
      metadata?: unknown
    }
  | {
      kind: "error"
      uri: RegisteredMetricUri
      detail: string
      code?: string
      location?: string
      model?: string
      provider?: string
      sessionID?: string
      traceID?: string
    }
  | { kind: "panic"; detail: string; stackTrace?: string; location?: string }

type Identity = { deviceId: string; userId: string }
type AuthDimensions = { tenantId?: string; departmentId?: string }
type AuthContext = { userId: string } & AuthDimensions

type Dependencies = {
  runtimeLoader: () => Promise<MetricsRuntimeModule>
  identityResolver: () => Promise<Identity>
  authContextResolver: (deviceId: string) => Promise<AuthContext>
  endpointResolver: () => string | Promise<string>
  trackingEnvResolver: () => ReturnType<typeof resolveMetricsTrackingEnv>
  panicEnabled: () => boolean
  flushIntervalMs: number
  maxPending: number
  shutdownTimeoutMs: number
}

const defaults: Dependencies = {
  runtimeLoader: loadMetricsRuntime,
  identityResolver: resolveMetricsIdentity,
  authContextResolver: resolveMetricsAuthContext,
  endpointResolver: resolveMetricsEndpoint,
  trackingEnvResolver: resolveMetricsTrackingEnv,
  panicEnabled: () => !isMetricsTestProcess(),
  flushIntervalMs: DEFAULT_FLUSH_INTERVAL_MS,
  maxPending: DEFAULT_MAX_PENDING,
  shutdownTimeoutMs: SHUTDOWN_TIMEOUT_MS,
}

type State = {
  chain: Promise<void>
  initPromise: Promise<void> | undefined
  runtime: MetricsRuntimeModule | undefined
  identity: Identity | undefined
  client: MetricsRuntimeClient | undefined
  endpoint: string | undefined
  disabled: boolean
  queuedCount: number
  queueCapWarned: boolean
  pendingSinceFlush: number
  capWarned: boolean
  flushTimer: Timer | undefined
  shutdownPromise: Promise<void> | undefined
  authDimensionMethodWarned: Partial<Record<AuthDimensionMethod, boolean>>
  authDimensionFallbackWarned: Partial<Record<AuthDimensionMethod, boolean>>
  legacyClientInfoWarned: boolean
}

function freshState(): State {
  return {
    chain: Promise.resolve(),
    initPromise: undefined,
    runtime: undefined,
    identity: undefined,
    client: undefined,
    endpoint: undefined,
    disabled: false,
    queuedCount: 0,
    queueCapWarned: false,
    pendingSinceFlush: 0,
    capWarned: false,
    flushTimer: undefined,
    shutdownPromise: undefined,
    authDimensionMethodWarned: {},
    authDimensionFallbackWarned: {},
    legacyClientInfoWarned: false,
  }
}

let dependencies: Dependencies = defaults
let state: State = freshState()

export namespace MetricsClient {
  export function configure(input: Partial<Dependencies>) {
    dependencies = {
      ...dependencies,
      ...input,
    }
  }

  /** 同步返回；处理挂到串行链上，任何路径都不会 throw 到调用方。 */
  export function record(spec: EventSpec): void {
    tryRecord(spec)
  }

  /** 同步返回是否接受进本地队列；不代表远端已送达。 */
  export function tryRecord(spec: EventSpec): boolean {
    try {
      if (state.disabled || state.shutdownPromise) return false
      // init 未完成期间串行链不消费，入队前先按 maxPending 约束内存（I1）
      if (state.queuedCount >= dependencies.maxPending) {
        if (!state.queueCapWarned) {
          state.queueCapWarned = true
          log.warn("metrics record queue cap reached, dropping events", {
            maxPending: dependencies.maxPending,
            uri: "uri" in spec ? spec.uri : spec.kind,
          })
        }
        return false
      }
      state.queuedCount += 1
      enqueue(async () => {
        try {
          await process_(spec)
        } finally {
          state.queuedCount -= 1
          if (state.queuedCount === 0) state.queueCapWarned = false
        }
      })
      return true
    } catch (err) {
      log.debug("metrics record failed", { error: stringifyError(err) })
      return false
    }
  }

  /** 退出前最后一次 flush（带超时）。幂等。**会永久停用记录**：置位 shutdownPromise 之后
   * tryRecord 一律返回 false，只应在进程真正即将退出时调用。关闭过程中间阶段请用 flush()。 */
  export function shutdown(): Promise<void> {
    state.shutdownPromise ??= doShutdown()
    return state.shutdownPromise
  }

  /**
   * 送出当前已入队的事件，但**不停用记录器**——之后的 tryRecord 仍然接受事件。
   *
   * 关闭链路的中间阶段（instance dispose 之前）需要这个：那时进程还活着，disposeEntry /
   * runDisposers / server.stop 都还会继续产生指标，而 shutdown() 一旦置位 shutdownPromise，
   * 这些后续事件会被 tryRecord 首行静默丢弃——恰好是关闭期埋点最该被保住的那一批。
   */
  export function flush(): Promise<void> {
    if (state.shutdownPromise) return state.shutdownPromise
    return enqueue(async () => {
      if (state.client) await flushNow(state.client)
    })
  }

  /** 认证地址变化后按队列顺序 flush 旧客户端，并让后续事件切到最新 endpoint。 */
  export function refreshEndpoint(): Promise<void> {
    if (state.shutdownPromise) return state.shutdownPromise
    return enqueue(refreshEndpoint_)
  }

  /** 等待串行链清空（测试用）。 */
  export async function drainForTest(): Promise<void> {
    await drain()
  }

  /** 当前串行链上未处理完的事件数（测试用，只读）。 */
  export function queuedCountForTest(): number {
    return state.queuedCount
  }

  /** 完整重置模块级状态（含 init 单飞 promise 与依赖），保证测试间隔离。 */
  export async function resetForTest(): Promise<void> {
    stopFlushTimer()
    // 注入的 loader 可能永远 pending（I1 回归测试），drain 带超时兜底避免挂死
    await Promise.race([drain().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 50))])
    stopFlushTimer()
    state = freshState()
    dependencies = defaults
  }
}

function enqueue(task: () => Promise<void>): Promise<void> {
  const next = state.chain.then(task).catch((err) => {
    log.debug("metrics task failed", { error: stringifyError(err) })
  })
  state.chain = next
  return next
}

async function drain() {
  // 链上的任务可能继续追加（如 panic 立即 flush），循环直到稳定
  for (;;) {
    const current = state.chain
    await current
    if (current === state.chain) return
  }
}

async function process_(spec: EventSpec) {
  if (state.disabled) return
  if (spec.kind === "panic" && !dependencies.panicEnabled()) return
  state.initPromise ??= init()
  await state.initPromise
  const client = state.client
  const runtime = state.runtime
  const cachedIdentity = state.identity
  if (state.disabled || !client || !runtime || !cachedIdentity) return

  if (state.pendingSinceFlush >= dependencies.maxPending) {
    if (!state.capWarned) {
      state.capWarned = true
      log.warn("metrics buffer cap reached, dropping events until next flush", {
        maxPending: dependencies.maxPending,
        uri: "uri" in spec ? spec.uri : spec.kind,
      })
    }
    return
  }

  const authContext = await resolveAuthContextForEvent(cachedIdentity)
  const identity = { deviceId: cachedIdentity.deviceId, userId: authContext.userId }
  const event = buildEvent(runtime, spec, identity, authContext)
  client.add_event(event)
  state.pendingSinceFlush += 1

  if (spec.kind === "panic") {
    await flushNow(client)
  }
}

async function init() {
  try {
    const runtime = await dependencies.runtimeLoader()
    const identity = await dependencies.identityResolver()
    const endpoint = await dependencies.endpointResolver()
    const client = createClient(runtime, identity, endpoint)
    state.runtime = runtime
    state.identity = identity
    state.client = client
    state.endpoint = endpoint
    startFlushTimer()
  } catch (err) {
    state.disabled = true
    log.warn("metrics runtime init failed, metrics disabled for this process", {
      error: stringifyError(err),
    })
  }
}

async function refreshEndpoint_() {
  const current = state.client
  const runtime = state.runtime
  const identity = state.identity
  if (!current || !runtime || !identity) return

  try {
    const next = await dependencies.endpointResolver()
    if (next === state.endpoint) return
    await flushNow(current)
    state.client = createClient(runtime, identity, next)
    state.endpoint = next
  } catch (err) {
    log.warn("metrics endpoint refresh failed, keeping current client", {
      error: stringifyError(err),
    })
  }
}

function createClient(runtime: MetricsRuntimeModule, identity: Identity, endpoint: string) {
  const platform = resolveMetricsPlatform()
  const os = resolveMetricsOs()
  const version = resolveMetricsVersion()
  const info = createClientInfo(runtime, platform, os, version, identity.deviceId)
  const trackingEnv = dependencies.trackingEnvResolver()
  const sdkEnv = resolveMetricsSdkEnv(runtime, trackingEnv)
  return new runtime.Client(info, sdkEnv, endpoint)
}

type LegacyClientInfoConstructor = new (
  appName: string,
  platform: string,
  version?: string | null,
  deviceId?: string | null,
) => unknown

function createClientInfo(
  runtime: MetricsRuntimeModule,
  platform: string,
  os: string,
  version: string,
  deviceId: string,
) {
  const ClientInfo = runtime.ClientInfo
  if (ClientInfo.length >= 5) return new ClientInfo(METRICS_APP_NAME, platform, os, version, deviceId)
  warnLegacyClientInfo(ClientInfo.length)
  const LegacyClientInfo = ClientInfo as unknown as LegacyClientInfoConstructor
  return new LegacyClientInfo(METRICS_APP_NAME, platform, version, deviceId)
}

async function resolveMetricsEndpoint() {
  const override = endpoint(process.env["INFCODE_EVENT_TRACKING_ENDPOINT"])
  if (override) return override
  const cfg = await AuthStorage.load()
  return endpoint(cfg.baseUrl) ?? AuthStorage.base
}

async function resolveMetricsAuthContext(deviceId: string): Promise<AuthContext> {
  const cfg = await AuthStorage.load()
  const userInfo = objectRecord(cfg.userInfo)
  const userId = userIdFrom(cfg.userInfo, deviceId)
  const tenantId = tenantIdFrom(userInfo?.tenant)
  const departmentId = departmentIdFrom(userInfo?.groups)
  return {
    userId,
    ...(tenantId ? { tenantId } : {}),
    ...(departmentId ? { departmentId } : {}),
  }
}

async function resolveAuthContextForEvent(identity: Identity): Promise<AuthContext> {
  try {
    return await dependencies.authContextResolver(identity.deviceId)
  } catch (err) {
    log.debug("metrics auth context resolve failed, using cached identity without auth dimensions", {
      error: stringifyError(err),
    })
    return { userId: identity.userId }
  }
}

function endpoint(value: string | undefined) {
  return value?.trim().replace(/\/+$/, "") || undefined
}

function departmentIdFrom(groups: unknown) {
  // 与 /acode/v1/userinfo 后端口径对齐：department_id 承载 groups 中第一个非空 group_name。
  if (!Array.isArray(groups)) return undefined
  for (const group of groups) {
    const groupName = groupNameFrom(group)
    if (groupName) return groupName
  }
  return undefined
}

function groupNameFrom(group: unknown) {
  if (typeof group === "string") return trimmedText(group)
  const record = objectRecord(group)
  return trimmedText(record?.group_name)
}

function tenantIdFrom(tenant: unknown) {
  if (typeof tenant === "string") return trimmedText(tenant)
  const record = objectRecord(tenant)
  return trimmedText(record?.tenant_id)
}

function startFlushTimer() {
  if (state.flushTimer) return
  const timer = setInterval(() => {
    if (!state.client) return
    enqueue(async () => {
      if (state.client) await flushNow(state.client)
    })
  }, dependencies.flushIntervalMs)
  timer.unref?.()
  state.flushTimer = timer
}

function stopFlushTimer() {
  if (!state.flushTimer) return
  clearInterval(state.flushTimer)
  state.flushTimer = undefined
}

async function flushNow(client: MetricsRuntimeClient) {
  try {
    await withTimeout(Promise.resolve(client.flush_front_end()), FLUSH_TIMEOUT_MS)
  } catch (err) {
    log.debug("metrics flush failed, events dropped", { error: stringifyError(err) })
  } finally {
    // SDK 语义（已核实 event_tracking_rs 源码）：flush_front_end() 入口即 drain 清空
    // 内部缓冲，HTTP 失败也只记日志并返回 Ok。因此无论成败，flush 后 SDK 缓冲必为空，
    // pendingSinceFlush 无条件归零是正确的，不存在"失败后 cap 失效"的问题。
    state.pendingSinceFlush = 0
    state.capWarned = false
  }
}

async function doShutdown() {
  try {
    stopFlushTimer()
    await withShutdownTimeout(async () => {
      await drain()
      if (state.client) await flushNow(state.client)
    })
  } catch (err) {
    log.debug("metrics shutdown failed", { error: stringifyError(err) })
  }
}

/** 硬超时兜底：drain 可能卡在 pending init，不得阻塞进程退出（设计 §6.1 / §10.3.5）。 */
async function withShutdownTimeout(run: () => Promise<void>) {
  let timer: Timer | undefined
  const timeoutPromise = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      log.debug("metrics shutdown timed out, proceeding with exit", {
        timeoutMs: dependencies.shutdownTimeoutMs,
      })
      resolve()
    }, dependencies.shutdownTimeoutMs)
    timer.unref?.()
  })
  try {
    await Promise.race([run(), timeoutPromise])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

function buildEvent(
  runtime: MetricsRuntimeModule,
  spec: EventSpec,
  identity: Identity,
  authDimensions: AuthDimensions,
): MetricsTraceEvent {
  if (spec.kind === "performance") {
    let builder = applyCommon(new runtime.PerformanceEventBuilder(spec.uri), identity, spec.sessionID, authDimensions)
    if (spec.value !== undefined) builder = applyBuilder(builder, "value", spec.value)
    if (spec.unit) builder = applyBuilder(builder, "unit", spec.unit)
    if (spec.status) builder = applyBuilder(builder, "status", STATUS_CODE[spec.status])
    if (spec.model) builder = applyBuilder(builder, "model", spec.model)
    if (spec.provider) builder = applyBuilder(builder, "provider", spec.provider)
    if (spec.metadata !== undefined) builder = applyBuilder(builder, "metadata", spec.metadata)
    return finalize(builder, spec.traceID, authDimensions)
  }
  if (spec.kind === "error") {
    let builder = applyCommon(
      new runtime.ErrorEventBuilder(spec.uri, sanitizeDetail(spec.detail, ERROR_DETAIL_LIMIT)),
      identity,
      spec.sessionID,
      authDimensions,
    )
    if (spec.code) builder = applyBuilder(builder, "code", spec.code)
    if (spec.location) builder = applyBuilder(builder, "location", spec.location)
    if (spec.model) builder = applyBuilder(builder, "model", spec.model)
    if (spec.provider) builder = applyBuilder(builder, "provider", spec.provider)
    return finalize(builder, spec.traceID, authDimensions)
  }
  if (spec.kind === "panic") {
    let builder = applyCommon(
      new runtime.PanicEventBuilder(sanitizeDetail(spec.detail, ERROR_DETAIL_LIMIT)),
      identity,
      undefined,
      authDimensions,
    )
    if (spec.stackTrace) builder = applyBuilder(builder, "stack_trace", sanitizeDetail(spec.stackTrace, PANIC_STACK_LIMIT))
    if (spec.location) builder = applyBuilder(builder, "location", spec.location)
    return finalize(builder, undefined, authDimensions)
  }
  return finalize(
    applyCommon(new runtime.PageViewEventBuilder(spec.uri), identity, spec.sessionID, authDimensions),
    spec.traceID,
    authDimensions,
  )
}

function applyCommon(
  builder: MetricsEventBuilder,
  identity: Identity,
  sessionID: string | undefined,
  authDimensions: AuthDimensions,
) {
  let next = applyBuilder(builder, "user_id", identity.userId)
  if (sessionID) next = applyBuilder(next, "session_id", sessionID)
  if (authDimensions.tenantId) next = applyBuilder(next, "tenant_id", authDimensions.tenantId)
  if (authDimensions.departmentId) next = applyBuilder(next, "department_id", authDimensions.departmentId)
  return next
}

type ChainableMethod = Exclude<keyof MetricsEventBuilder, "build" | "free">
type AuthDimensionMethod = "tenant_id" | "department_id"

/** 方法存在才调用（wasm 版本差异防御），缺失时原样返回 builder。 */
function applyBuilder(builder: MetricsEventBuilder, method: ChainableMethod, value: unknown) {
  const apply = builder[method]
  if (typeof apply !== "function") return builder
  return (apply as (input: unknown) => MetricsEventBuilder).call(builder, value)
}

/** trace_id 仅 PerformanceEventBuilder 提供；其余 builder 在 build() 出的 TraceEvent 上回填。 */
function finalize(
  builder: MetricsEventBuilder,
  traceID: string | undefined,
  authDimensions: AuthDimensions,
): MetricsTraceEvent {
  let next = builder
  if (traceID && typeof builder.trace_id === "function") {
    next = builder.trace_id(traceID)
  }
  const built = next.build()
  if (traceID) built.trace_id = traceID
  fallbackAuthDimension(built, builder, "tenant_id", authDimensions.tenantId)
  fallbackAuthDimension(built, builder, "department_id", authDimensions.departmentId)
  return built
}

function fallbackAuthDimension(
  built: MetricsTraceEvent,
  builder: MetricsEventBuilder,
  method: AuthDimensionMethod,
  value: string | undefined,
) {
  if (!value || typeof builder[method] === "function") return
  warnMissingAuthDimensionMethod(method)
  try {
    built[method] = value
  } catch (err) {
    warnAuthDimensionFallbackFailed(method, err)
  }
}

function warnMissingAuthDimensionMethod(method: AuthDimensionMethod) {
  if (state.authDimensionMethodWarned[method]) return
  state.authDimensionMethodWarned[method] = true
  log.warn("metrics runtime builder missing auth dimension method, falling back to TraceEvent assignment", { method })
}

function warnLegacyClientInfo(actualArity: number) {
  if (state.legacyClientInfoWarned) return
  state.legacyClientInfoWarned = true
  log.warn("metrics runtime ClientInfo missing os parameter, falling back to legacy constructor", {
    actualArity,
    expectedArity: 5,
  })
}

function warnAuthDimensionFallbackFailed(method: AuthDimensionMethod, err: unknown) {
  if (state.authDimensionFallbackWarned[method]) return
  state.authDimensionFallbackWarned[method] = true
  log.warn("metrics auth dimension fallback failed, recording without dimension", {
    method,
    error: stringifyError(err),
  })
}

async function withTimeout<T>(promise: Promise<T>, timeout: number): Promise<T> {
  let timer: Timer | undefined
  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`metrics flush timeout after ${timeout}ms`)), timeout)
    timer.unref?.()
  })
  try {
    return await Promise.race([promise, timeoutPromise])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

function stringifyError(err: unknown) {
  return err instanceof Error ? err.message : String(err)
}
